/**
 * The fx-widget wire protocol: everything a custom widget and its host exchange.
 *
 * This file is the portability boundary of the whole custom-widget system. The
 * host (Angular today, anything tomorrow) and the widget bundle (any framework)
 * only ever share these JSON message shapes over a MessageChannel — never code,
 * never framework objects. Breaking changes require a new protocol version
 * string, not an edit to fx-widget/1 shapes.
 */

export const WIDGET_PROTOCOL = 'fx-widget/1';

/**
 * The version of *this* SDK, stamped into a bundle's widget.json by the packaging
 * step so the platform can tell what a compiled bundle was built against.
 *
 * It has to be a hand-maintained constant for now: the SDK is vendored/imported
 * from source rather than published to npm, so there is no package version to
 * read, and the SDK is compiled *into* the bundle — once packaged, nothing about
 * the JS says which SDK produced it. widget.json is the only place that fact can
 * survive, and this constant is the only honest source for it.
 *
 * **1.0.0 is the first App SDK release.** The predecessor Widget SDK, whose
 * version constant was frozen at 1.1.0, was never published and no third-party
 * bundle was ever built against it, so the rename to the App SDK restarts the
 * numbering rather than carrying a version nobody could observe (ADR 0003,
 * amendment 2026-08-25). Pre-release surface changes are recorded by updating
 * BASELINE_SURFACE_SHA256 in the contract-surface baseline test
 * (compatibility.spec.ts) and nothing else. Once bundles built by third parties
 * exist, this starts moving as semver — minor for additive changes — and
 * `WIDGET_PROTOCOL` stays what it is: a *wire* version naming the widget
 * transport, not this package, that changes only on a break so fundamental that
 * old and new cannot share a host.
 */
export const APP_SDK_VERSION = '0.1.0';

/**
 * The oldest bundle SDK version this host still runs. Bundles stamped below it
 * are refused at upload, at activation and at render rather than being loaded and
 * left to fail in some undiagnosable way inside the frame.
 *
 * It sits at 1.0.0, the first App SDK version there is, so nothing is stranded.
 * Raising it is a deliberate act that strands published bundles, so it must be
 * accompanied by an SDK_BREAKING_CHANGES entry saying why.
 */
export const MINIMUM_COMPATIBLE_SDK_VERSION = '0.1.0';

/**
 * One entry per change that makes older bundles genuinely unable to run, so a
 * rejection can say *why* instead of only quoting two version numbers.
 *
 * `since` is the SDK version that introduced the break: a bundle stamped with
 * anything **older than** `since` is affected by it.
 *
 * Empty, and correctly so: 1.0.0 is the first App SDK release, so there is no
 * earlier version for anything to have broken *since*. The Widget SDK entries
 * this list would have inherited went with the rename — nothing was ever
 * published against it. Adding an entry here is how a real break gets recorded;
 * inventing one would make MINIMUM_COMPATIBLE_SDK_VERSION lie.
 */
export const SDK_BREAKING_CHANGES: { since: string; reason: string }[] = [];

/** Snapshot of the environment a widget instance renders in. */
export interface WidgetContext {
  widgetId: string;
  instanceId: string;
  /** Instance settings from the builder, validated against the manifest's settingsSchema. */
  settings: Record<string, unknown>;
  page: { zone: string; url: string; role: string };
  /** Design tokens (CSS custom property name -> value), not a stylesheet. */
  theme: Record<string, string>;
  locale: string;
  user: { id: string; roles: string[] };
}

/**
 * What a widget was granted at activation time. The host bridge hard-rejects
 * any call outside these; the SDK client mirrors them for early feedback only.
 */
export interface CapabilityGrants {
  /** Data topics readable/subscribable, e.g. 'accounts'. */
  data: string[];
  /** Named operations callable, e.g. 'payments.initiate'. */
  api: string[];
  forms: boolean;
  nav: boolean;
  /**
   * Origins the widget may contact directly with fetch/XHR/WebSocket, e.g.
   * 'https://api.weather.example'. Enforced by the CSP the platform serves the
   * bundle with, not by the bridge. Absent/empty = no network egress at all.
   *
   * The scheme is part of the grant, not decoration: these entries become CSP
   * `connect-src` sources, and a source whose scheme is `https` does not match a
   * `wss` connection. A widget that opens a WebSocket therefore needs its own
   * `wss://host` entry as well as (or instead of) the `https://host` one —
   * `['https://api.example']` alone will not open a socket to api.example.
   *
   * `connect-src` is all this grant opens. Remote images, fonts, stylesheets and
   * prefetches stay blocked even for a granted origin (`img-src 'self' data:`,
   * `prefetch-src 'none'`), so assets still have to be bundled or inlined.
   */
  network?: string[];
  /** Native browser permissions delegated to this frame by Permissions Policy. */
  browser?: BrowserCapability[];
  /** Durable backend documents this widget may read or replace. */
  storage?: { read: string[]; write: string[] };
  /**
   * Backend functions this widget may invoke by slug, e.g. 'referral-score'.
   * Deny-by-default and per slug: an entry authorises that one function and
   * nothing else, and an absent/empty list means the widget cannot invoke any.
   *
   * A function runs server-side **as the signed-in user** — the host attaches
   * the session, never the widget — so granting a slug delegates whatever that
   * function is allowed to do to this widget. The functions themselves are
   * project-side: they are published and activated on the project, not declared
   * by this manifest, and a granted slug that is not activated answers
   * 'unknown-operation' rather than being created on demand.
   */
  functions?: string[];
}

export type WidgetStorageScope = 'user' | 'organisation';

/**
 * What a definition holds. `document` is the original shape — at most one record
 * per definition per subject, served by `records.get`/`create`/`update`/`delete`.
 * `collection` holds many rows addressed by `recordId`, served by the
 * `records.items.*` five.
 *
 * The two are mutually exclusive: calling a document operation on a collection,
 * or an item operation on a document, is refused as `wrong-shape` rather than
 * being reinterpreted.
 */
export type DataDefinitionShape = 'document' | 'collection';

/**
 * One filter clause of a collection query. `field` is a path into the record's
 * `value` ('status', 'customer.id'), never a column of the row itself.
 *
 * `in` takes an array and is capped at DATA_RECORD_QUERY_MAX_IN_VALUES values;
 * the range operators take a single comparable value.
 */
export interface RecordFilter {
  field: string;
  op: 'eq' | 'in' | 'lt' | 'lte' | 'gt' | 'gte';
  value: unknown;
}

/** Sort clause of a collection query. Applied in the order given. */
export interface RecordSort {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * One row of a collection definition. `recordId` is the handle every item
 * operation takes, `revision` is the optimistic guard (`ifRevision`), and
 * `value` is the author's own JSON, validated against the definition's schema.
 *
 * `ownerUserId` is present only where the definition's scope makes ownership
 * meaningful — an organisation-scoped collection whose rows were written by
 * different people.
 */
export interface DataRecord<T extends Record<string, unknown> = Record<string, unknown>> {
  recordId: string;
  definition: string;
  revision: number;
  value: T;
  createdAt: string;
  updatedAt: string;
  ownerUserId?: string;
}

/**
 * A page of a collection. `nextCursor` is opaque: hand it back untouched as
 * `cursor` for the next page, and `null` means this was the last one.
 */
export interface RecordPage<T extends Record<string, unknown> = Record<string, unknown>> {
  items: DataRecord<T>[];
  nextCursor: string | null;
}

/** The limits a host enforces on collections, published so an application can pre-empt them. */
export const DATA_RECORD_MAX_PER_COLLECTION = 100_000;
export const DATA_RECORD_QUERY_MAX_LIMIT = 200;
export const DATA_RECORD_QUERY_DEFAULT_LIMIT = 50;
export const DATA_RECORD_QUERY_MAX_IN_VALUES = 20;
export const DATA_RECORD_MAX_BYTES = 64 * 1024;

/** A versioned document definition supplied by Data Studio. */
export interface DataDefinition {
  reference: string;
  definitionId: string;
  version: number;
  name: string;
  description: string;
  scope: WidgetStorageScope;
  schema: Record<string, unknown>;
  deprecated?: boolean;
  access: { read: boolean; write: boolean };
  /**
   * Optional purely for history: a host that predates collections answers no
   * shape at all, and an absent shape means 'document' — the only thing such a
   * host can serve.
   */
  shape?: DataDefinitionShape;
  /**
   * Collections only. The field sets the store can answer a query over; a
   * `where` or `orderBy` field outside every index is refused
   * ('query-unindexed-field') rather than answered by a scan.
   */
  indexes?: { fields: string[] }[];
  /**
   * Collections only. Alternative sets of fields, at least one of which a query
   * must filter on ('query-missing-required-filter' otherwise). A tenant-scoped
   * collection uses this to make an unscoped query impossible to write.
   */
  requiredQueryFilters?: string[][];
}

export interface DataDocument<T extends Record<string, unknown> = Record<string, unknown>> {
  value: T;
  revision: number;
  updatedAt: string;
}

export const BROWSER_CAPABILITIES = ['geolocation', 'camera', 'microphone'] as const;
export type BrowserCapability = (typeof BROWSER_CAPABILITIES)[number];

/** Manifest packaged with every widget bundle as widget.json. */
export interface WidgetManifest {
  protocol: typeof WIDGET_PROTOCOL;
  id: string;
  name: string;
  version: string;
  /**
   * The SDK the bundle was built against — `APP_SDK_VERSION` at packaging
   * time. **Tooling-owned: never hand-written and never edited by an author.**
   * The registry gates uploads, activations and renders on it (see
   * compatibility.ts), which only works if it describes the SDK that is actually
   * compiled into the bundle.
   *
   * Optional in the type purely for history: manifests stored before the field
   * existed have none, and are read as GRANDFATHERED_SDK_VERSION. New uploads
   * are required to carry it.
   */
  sdkVersion?: string;
  entry: string;
  capabilities: CapabilityGrants;
  /**
   * Author-supplied prose describing what the widget does. Optional and purely
   * descriptive — nothing gates on it. Surfaced in the system-wide widget gallery
   * (card body + free-text search). Grandfathered: manifests without it are fine.
   */
  description?: string;
  /**
   * Author-supplied search tags (e.g. ['payments', 'sepa', 'faster-payments']).
   * Optional; used only to make the widget findable in the gallery's search.
   * Grandfathered: manifests without it are fine.
   */
  keywords?: string[];
  /** JSON schema for instance settings; drives the builder's config panel. */
  settingsSchema?: Record<string, unknown>;
  size?: { defaultHeight?: number };
}

/**
 * Handshake: the host posts this to the frame's window with a transferred
 * MessagePort; all subsequent traffic flows over that port. The widget replies
 * with { type: 'ready' } on the port once mounted.
 */
export interface InitMessage {
  protocol: typeof WIDGET_PROTOCOL;
  type: 'init';
  context: WidgetContext;
  grants: CapabilityGrants;
}

/** Widget -> host method call. Methods are the namespaced strings below. */
export interface RequestMessage {
  type: 'request';
  id: number;
  method: WidgetMethod;
  params: Record<string, unknown>;
}

export interface ResponseMessage {
  type: 'response';
  id: number;
  ok: boolean;
  result?: unknown;
  /**
   * Present when ok is false.
   *
   * `details` carries the machine-readable part of a rejection a widget has to
   * *act* on rather than merely display — the seconds to wait before a resend
   * ('rate-limited'), the steps still outstanding
   * ('onboarding-prerequisite-unmet'). It is deliberately a bag rather than a
   * union: the per-code shapes live with the operations that raise them (see
   * WidgetRateLimitedDetails / WidgetPrerequisiteUnmetDetails in contracts.ts),
   * and a host that has nothing structured to add simply omits it. Never put a
   * secret, a token or a provider internal in here — it crosses to the bundle.
   */
  error?: {
    code: WidgetErrorCode;
    message: string;
    details?: Record<string, unknown>;
    /** The platform's own refusal slug behind `code`, when it has one (WidgetOperationError.serverCode). */
    serverCode?: string;
  };
}

/** Push message, host -> widget (data topics, context updates). */
export interface StreamMessage {
  type: 'stream';
  stream: string;
  payload: unknown;
}

export interface ReadyMessage {
  type: 'ready';
}

export type PortMessage = RequestMessage | ResponseMessage | StreamMessage | ReadyMessage;

/**
 * Widget -> host methods.
 *
 * - 'data.*'   read/subscribe a data topic (grant: `data`).
 * - 'api.call' invoke a named operation (grant: `api`).
 * - 'forms.*'  read/write the surrounding page's forms (grant: `forms`).
 * - 'nav.*'    navigate the host app (grant: `nav`).
 * - 'ui.*'     host-rendered notifications/confirmations (always allowed).
 * - 'events.emit' / 'resize' host-observed signals (always allowed).
 *
 * - 'handoff.start' — params `{ kind, params }`, resolves
 *   `{ handoffId }`. Declares a *hand-off*: an operation that cannot suspend an
 *   api.call because it completes somewhere the widget cannot reach (a PISP
 *   deposit at the customer's own bank, an AISP link, a KYC vendor session).
 *   The widget declares the intent and the host owns everything else — it
 *   persists `{ handoffId, kind, params, status: 'pending' }` for this widget
 *   instance, calls whatever service mints the destination URL, and hands
 *   control over.
 *
 *   Resolution means exactly one thing: **the host has accepted the hand-off
 *   and begun it.** It says nothing about this document, this frame or this
 *   widget, because *how* the hand-off runs is the host's business and is
 *   deliberately unobservable: one host redirects the whole page, another runs
 *   it in a shell-owned webview or an external browser, another in a native
 *   modal. So the widget MUST NOT assume it will be torn down, and MUST NOT
 *   assume it will survive.
 *
 *   The single source of truth for the outcome is the 'handoffs' data topic
 *   (instance-scoped by the host, so a widget only ever sees its own
 *   hand-offs). A correct widget does both of these, on every mount: read
 *   'handoffs' with data.get, and stay subscribed with data.subscribe. If the
 *   widget survived the hand-off it receives a push when the record reaches a
 *   terminal status; if it was torn down it reads the completed record on the
 *   next mount. Both are equally normal — neither is a fallback for the other,
 *   and a widget that implements only one is broken on some host.
 *
 *   Because teardown is possible, anything the widget needs afterwards must
 *   already be in 'state.set' BEFORE start() is called — never scheduled after
 *   it resolves.
 *
 *   Grant: deny-by-default per *kind*, granted as the api entry
 *   `handoff:<kind>` (see handoffGrant) — a plain api grant never authorises a
 *   hand-off and one granted kind never authorises another.
 *
 * - 'state.get' / 'state.set' — the widget's own scratchpad, so a half-filled
 *   form survives a hand-off round trip (and ordinary navigations and
 *   refreshes). set() replaces the whole value; get() resolves undefined until
 *   something is stored. Always allowed and never granted: it is the widget's
 *   own data, scoped by the host to this widget instance, so one instance can
 *   never read another's scratchpad. Not a cache for platform data and not a
 *   channel between widgets.
 *
 * - 'functions.invoke' — params `{ slug, payload }`, resolves the function's own
 *   output. Calls a *backend function* the project published: code that runs
 *   server-side, as the signed-in user, with the host attaching the session the
 *   widget never sees. Grant: deny-by-default per slug, as a `functions` entry
 *   (never an `api` one) — the api list names platform operations this SDK
 *   defines, and a project's functions are neither defined here nor stable
 *   across projects, so mixing them would make one grant list mean two things.
 *
 *   Input and output are the function's business: the SDK carries them through
 *   as `TIn`/`TOut` and validates neither. The function's manifest declares JSON
 *   schemas for both, and the platform enforces them — a payload that fails the
 *   input schema rejects with 'schema-validation' before any author code runs.
 */
export type WidgetMethod =
  | 'data.get'
  | 'data.subscribe'
  | 'data.unsubscribe'
  | 'api.call'
  | 'forms.get'
  | 'forms.set'
  | 'forms.validity'
  | 'nav.goToPage'
  | 'nav.back'
  | 'ui.notify'
  | 'ui.confirm'
  | 'events.emit'
  | 'handoff.start'
  | 'state.get'
  | 'state.set'
  | 'storage.get'
  | 'storage.set'
  | 'storage.remove'
  | 'storage.catalog'
  | 'functions.invoke'
  | 'resize';

export type WidgetErrorCode =
  | 'capability-denied'
  | 'unknown-method'
  | 'unknown-topic'
  | 'unknown-operation'
  /**
   * The operation required a user confirmation (SCA) and the user declined or
   * it timed out. The host runs its confirmation UI without unmounting the
   * widget; the original call stays pending until then.
   */
  | 'confirmation-declined'
  /**
   * The host refused the call because the *platform* is rate-limiting it, not
   * because anything is wrong with the request — the OTP send endpoints allow 3
   * per minute per IP. `error.details` carries
   * WidgetRateLimitedDetails ({ retryAfterSeconds }), because a resend button
   * needs exactly that number and nothing else. A widget MUST NOT retry on a
   * timer of its own choosing.
   */
  | 'rate-limited'
  /**
   * An onboarding step was attempted out of order — a submit before both
   * contact channels were verified, an identity check before a submit.
   * `error.details` carries WidgetPrerequisiteUnmetDetails
   * ({ outstanding: WidgetOnboardingStepId[] }) so the widget can say which
   * step to go back to.
   *
   * Raised by the host *before* any HTTP call, so it is not an observation of a
   * server rejection: it is the bridge enforcing the same gate the server
   * enforces, early enough that nothing happened. The server check remains the
   * real authority.
   */
  | 'onboarding-draft-unavailable'
  | 'product-unavailable'
  | 'otp-expired'
  | 'cop-expired'
  | 'application-failed'
  | 'onboarding-prerequisite-unmet'
  /**
   * The operation payload failed the platform's own validation rules — the
   * host runs the native route's DTO over the mapped request before anything
   * executes, so nothing happened. `message` names the offending wire fields
   * and their constraint messages; `error.details` carries
   * WidgetInvalidPayloadDetails ({ fields: [{ field, constraints }] }) for
   * widgets that want to highlight the field rather than parse the message.
   * Part of the surface from App SDK 1.0.0. An unrecognised code should always
   * be treated as the failure it describes: a host that does not raise this one
   * reports the same condition as 'host-error' with the same message.
   */
  | 'invalid-payload'
  | 'schema-validation'
  | 'conflict'
  | 'host-error';

/**
 * The finer refusals the records surface names, and the vocabulary code each
 * one is reported as.
 *
 * They are *reasons*, not codes: the wire envelope carries the reason (the same
 * way it already carries 'scope-denied' or 'not-activated'), and both transports
 * report the mapped `WidgetErrorCode` with the reason echoed in
 * `error.details.reason`. Adding six codes to the vocabulary instead would have
 * made every host switch over refusals only one operation family can raise.
 *
 *  - 'wrong-shape'                   a document operation on a collection definition, or the reverse
 *  - 'collection-full'               DATA_RECORD_MAX_PER_COLLECTION reached; retrying cannot help
 *  - 'invalid-revision'              `ifRevision` did not match the stored revision
 *  - 'payload-too-large'             the record exceeds DATA_RECORD_MAX_BYTES
 *  - 'schema-validation'             the value failed the definition's published schema
 *  - 'query-missing-required-filter' none of `requiredQueryFilters` was satisfied
 *  - 'query-unindexed-field'         a `where`/`orderBy` field no declared index covers
 *  - 'query-unknown-field'           a field the definition's schema does not declare
 *  - 'query-too-many-values'         an `in` clause over DATA_RECORD_QUERY_MAX_IN_VALUES values
 */
export const DATA_RECORD_ERROR_REASONS = {
  'wrong-shape': 'invalid-payload',
  'collection-full': 'conflict',
  'invalid-revision': 'conflict',
  'payload-too-large': 'invalid-payload',
  'schema-validation': 'schema-validation',
  'query-missing-required-filter': 'invalid-payload',
  'query-unindexed-field': 'invalid-payload',
  'query-unknown-field': 'invalid-payload',
  'query-too-many-values': 'invalid-payload',
  'invalid-cursor': 'invalid-payload',
  'record-not-found': 'invalid-payload',
} as const satisfies Record<string, WidgetErrorCode>;

export type DataRecordErrorReason = keyof typeof DATA_RECORD_ERROR_REASONS;

/** Streams the host may push without a subscription. */
export const CONTEXT_STREAMS = ['theme', 'settings', 'context'] as const;
export type ContextStream = (typeof CONTEXT_STREAMS)[number];

/** Stream name carrying pushes for a subscribed data topic. */
export function dataStream(topic: string): string {
  return `data:${topic}`;
}

/**
 * Prefix marking a hand-off kind inside CapabilityGrants.api. Hand-offs are
 * side-effecting operations, so they live in the api grant list rather than a
 * list of their own; the prefix keeps them a distinct namespace, so granting
 * `payments.initiate` can never be read as granting `handoff:payments.initiate`
 * (and vice versa).
 */
export const HANDOFF_GRANT_PREFIX = 'handoff:';

/** The api grant entry that authorises starting hand-offs of `kind`. */
export function handoffGrant(kind: string): string {
  return `${HANDOFF_GRANT_PREFIX}${kind}`;
}

export interface UiNotification {
  type: 'success' | 'error' | 'info';
  message: string;
}

export interface UiConfirmation {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export function isInitMessage(data: unknown): data is InitMessage {
  const msg = data as InitMessage | null;
  return !!msg && msg.type === 'init' && msg.protocol === WIDGET_PROTOCOL;
}
